import * as THREE from 'three/webgpu';
import {collectImportedModelTopologyRepositories} from '../topology/ImportedModelTopologyRepository';

/**
 * 释放已由参数化特征接管的导入实体显示。
 *
 * 设计说明（方案 A：参数化重建为唯一真相源）：
 * 1. 离线包重开时，GLB 只承担两件事——重放完成前的即时显示，以及纯导入实体（无特征产出）的长期显示；
 * 2. 一旦某实体由特征命令重放产出（Worker 结果对象携带 commandId），它的显示必须交给 Worker 结果体，
 *    否则同一 objectId 会出现“导入 GLB 与结果体”两个所有者，导致编辑预览双体、提交后几何不更新；
 * 3. 交接方式是从导入拓扑仓库释放该实体记录并移除其可见网格：注册表的导入表示随之注销，
 *    后续同步不会再把它登记回来——这不是临时压制，而是所有权的一次性转移。
 *
 * @param scene 当前 Viewer 场景。
 * @param featureOwnedObjectIds 由特征命令产出的实体 ID 集合。
 * @returns 实际释放的导入实体数量。
 */
export function releaseFeatureOwnedImportedBodies(
    scene: THREE.Object3D,
    featureOwnedObjectIds: ReadonlySet<string>,
): number {
    if (featureOwnedObjectIds.size === 0) {
        return 0;
    }

    let releasedCount = 0;
    collectImportedModelTopologyRepositories(scene).forEach((repository) => {
        const releasedBodyBindingIds: string[] = [];
        const releasedVisibleMeshes: THREE.Mesh[] = [];

        repository.listBodyRecords().forEach((bodyRecord) => {
            const geometryObjectId = repository.resolveGeometryObjectIdForBodyRecord(bodyRecord).trim();
            if (!geometryObjectId || !featureOwnedObjectIds.has(geometryObjectId)) {
                return;
            }
            releasedBodyBindingIds.push(bodyRecord.bodyBindingId);
            releasedVisibleMeshes.push(bodyRecord.visibleMesh);
        });

        if (releasedBodyBindingIds.length === 0) {
            return;
        }

        releasedCount += repository.disposeBodyRecords(releasedBodyBindingIds);

        /**
         * 可见网格可能被多个 body 记录共享（同一 GLB mesh 承载多实体）；
         * 仅当仓库内已无任何记录引用该网格时才从场景移除并释放显存。
         */
        const retainedVisibleMeshes = new Set(
            repository.listBodyRecords().map((bodyRecord) => bodyRecord.visibleMesh),
        );
        releasedVisibleMeshes.forEach((visibleMesh) => {
            if (retainedVisibleMeshes.has(visibleMesh)) {
                return;
            }
            visibleMesh.removeFromParent();
            visibleMesh.geometry?.dispose?.();
        });
    });

    return releasedCount;
}
